import { PopupStateEntry, PopupStorageState } from '~types/PopupStorage.types';

const STORAGE_KEY = 'vkcloud_popup_state';
const SESSION_MARKER_KEY = 'vkcloud_popup_session';

const INITIAL_STATE: PopupStorageState = {
  nextPopupShowTime: 0,
  isNew: true,
  popups: {},
};

export const PopupStorage = {
  read(): PopupStorageState {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);

      if (!raw) return { ...INITIAL_STATE };

      const parsed = JSON.parse(raw) as PopupStorageState;

      return {
        nextPopupShowTime: parsed.nextPopupShowTime ?? 0,
        isNew: parsed.isNew ?? true,
        popups: parsed.popups ?? {},
      };
    } catch {
      return { ...INITIAL_STATE };
    }
  },

  write(state: PopupStorageState): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error(error);
    }
  },

  determineSegment(): 'new' | 'returned' {
    const storedSegment = window.sessionStorage.getItem(SESSION_MARKER_KEY);

    if (storedSegment === 'new' || storedSegment === 'returned') {
      return storedSegment;
    }

    const raw = window.localStorage.getItem(STORAGE_KEY);
    let segment: 'new' | 'returned';

    if (!raw) {
      this.write({ ...INITIAL_STATE });
      segment = 'new';
    } else {
      const state = this.read();

      if (state.isNew) {
        state.isNew = false;
        this.write(state);
        segment = 'new';
      } else {
        segment = 'returned';
      }
    }

    window.sessionStorage.setItem(SESSION_MARKER_KEY, segment);

    return segment;
  },

  isCooldownActive(): boolean {
    return Date.now() < this.read().nextPopupShowTime;
  },

  setCooldown(cooldownSeconds: number): void {
    const state = this.read();
    state.nextPopupShowTime = Date.now() + cooldownSeconds * 1000;
    this.write(state);
  },

  getPopupState(documentId: string): PopupStateEntry | null {
    return this.read().popups[documentId] ?? null;
  },

  recordShow(documentId: string, maxShows: number): void {
    const state = this.read();

    if (!state.popups[documentId]) {
      state.popups[documentId] = {
        remainingShows: maxShows,
        completedTargetActions: [],
        allTargetsCompleted: false,
        lastShownAt: null,
      };
    }

    state.popups[documentId].lastShownAt = new Date().toISOString();

    this.write(state);
  },

  recordClose(documentId: string): void {
    const state = this.read();
    const entry = state.popups[documentId];

    if (!entry) return;

    if (entry.remainingShows > 0) {
      entry.remainingShows -= 1;
    }

    this.write(state);
  },

  recordTargetAction(
    documentId: string,
    actionValue: string,
    allTargetActions: string[],
  ): boolean {
    const state = this.read();
    const entry = state.popups[documentId];

    if (!entry) return false;

    if (!entry.completedTargetActions.includes(actionValue)) {
      entry.completedTargetActions.push(actionValue);
    }

    entry.allTargetsCompleted = allTargetActions.every((ta) =>
      entry.completedTargetActions.includes(ta),
    );

    if (entry.allTargetsCompleted) {
      entry.remainingShows = 0;
    }

    this.write(state);

    return entry.allTargetsCompleted;
  },

  isPermanentlyDismissed(documentId: string): boolean {
    const entry = this.getPopupState(documentId);

    if (!entry) return false;

    return entry.allTargetsCompleted || entry.remainingShows <= 0;
  },

  cleanupStaleEntries(activeDocumentIds: string[]): void {
    const state = this.read();
    const activeSet = new Set(activeDocumentIds);
    let changed = false;

    for (const key of Object.keys(state.popups)) {
      if (!activeSet.has(key)) {
        delete state.popups[key];
        changed = true;
      }
    }

    if (changed) {
      this.write(state);
    }
  },
};
